import { useEffect, useState, useRef } from "react";
import { DASHBOARD_NAVIGATION_OPTIONS, NAVIGATION_SECTIONS } from "../../../constants";
import NavigationItem from "../NavigationItem";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useScreen from "../../../hooks/useScreen";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import { updateMenuDrawer } from "../../../reducer/appReducer";

const activeMenuWithHotelFilters = ["Dashboard", "Bookings", "Reports"];

const getActiveSectionKey = (options, activeMenu) => {
  const match = options.find(
    (o) => o.title?.toLowerCase() === activeMenu?.toLowerCase()
  );
  return match?.section ?? null;
};

// ── Expanded sidebar — section heading with chevron ───────────────────────────
const SectionLabel = ({ label, isOpen, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between px-[20px] pt-[16px] pb-[6px] cursor-pointer group"
    data-testid="section-label-button"
  >
    <span className="text-white/80 text-[11px] font-bold uppercase tracking-[0.12em] font-outfit group-hover:text-white transition-colors">
      {label}
    </span>
    {isOpen ? (
      <BiChevronUp className="text-white/60 text-[16px] group-hover:text-white transition-colors" />
    ) : (
      <BiChevronDown className="text-white/60 text-[16px] group-hover:text-white transition-colors" />
    )}
  </button>
);

// ── Collapsed sidebar — icon + truncated label (Zoho-style) ───────────────────
const CollapsedSectionIcon = ({ section, isActive, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`collapsed-section-icon-${section.key}`}
    className={`w-full flex flex-col items-center justify-center py-[10px] px-[6px] cursor-pointer transition-colors ${
      isActive ? "bg-[#00000026]" : "hover:bg-[#00000026]"
    }`}
  >
    <img src={section.icon} alt={section.label} className="w-[20px] h-[20px]" />
    <span className="text-white/80 text-[9px] font-outfit mt-[4px] w-full text-center leading-tight truncate">
      {section.label}
    </span>
  </button>
);

// ── Flyout — item row (white text, blue bg — extends the sidebar) ─────────────
const FlyoutMenuItem = ({ option, activePosSlug, dashboardSelectHandler, activeMenu, onSelect, createBookingAllowed, isAllHotelsSelected }) => {
  const navigate = useNavigate();
  const { hotelContentDetails } = useSelector((store) => store.login);

  const handleClick = () => {
    dashboardSelectHandler(option);

    if (option.link === "booking-engine") {
      window.open(
        `https://${hotelContentDetails?.bookingEngineSubdomain}.sites.revivotech.in`,
        "_newtab"
      );
    } else {
      const link =
        option.title?.toLowerCase() === "pos"
          ? `/dashboard/${activePosSlug}/`
          : `/dashboard/${option.link}`;
      navigate(link);
    }

    onSelect();
  };

  const isActive = activeMenu?.toLowerCase() === option.title?.toLowerCase();

  // + icon for Bookings → walkin, POS → table-view (matches expanded sidebar behaviour)
  const showAddButton =
    (option.link === "bookings" || option.link === "bookingReservation") &&
    createBookingAllowed && !isAllHotelsSelected;

  const showPosButton =
    option.link === "all-orders" && !isAllHotelsSelected;

  return (
    <div
      onClick={handleClick}
      className={`pl-[20px] pr-[10px] py-[10px] cursor-pointer text-white text-[13px] font-outfit transition-colors flex items-center justify-between ${
        isActive ? "bg-[#00000033] font-semibold" : "hover:bg-[#00000026]"
      }`}
      data-testid={`flyout-item-${option.id}`}
    >
      <span>{option.title}</span>

      {showAddButton && (
        <div
          className="w-[1.6rem] h-[1.6rem] flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            navigate("walkin");
            onSelect();
          }}
          data-testid="flyout-walkin-btn"
        >
          <img
            src={require("../../../assets/images/svgs/BookingAddSymbol.svg").default}
            alt="walkin"
            className="w-full h-full"
          />
        </div>
      )}

      {showPosButton && (
        <div
          className="w-[1.6rem] h-[1.6rem] flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/${activePosSlug}/table-view`);
            onSelect();
          }}
          data-testid="flyout-tableview-btn"
        >
          <img
            src={require("../../../assets/images/svgs/BookingAddSymbol.svg").default}
            alt="table view"
            className="w-full h-full"
          />
        </div>
      )}
    </div>
  );
};

// ── Flyout panel — same blue gradient as sidebar, seamless extension ───────────
const SectionFlyout = ({
  section,
  sectionItems,
  activePosSlug,
  dashboardSelectHandler,
  activeMenu,
  top,
  onMouseEnter,
  onMouseLeave,
  onSelect,
  createBookingAllowed,
  isAllHotelsSelected,
}) => (
  <div
    className="fixed z-[500] min-w-[180px] py-[8px] overflow-hidden rounded-r-lg shadow-xl"
    style={{
      left: "5rem",
      top,
      background: "linear-gradient(180deg, #007fff 0%, #2a53e4 100%)",
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    data-testid={`section-flyout-${section.key}`}
  >
    <p className="px-[20px] pt-[2px] pb-[6px] text-[10px] font-bold uppercase tracking-widest text-white/50 font-outfit border-b border-white/10 mb-[4px]">
      {section.label}
    </p>
    {sectionItems.map((option) => (
      <FlyoutMenuItem
        key={option.id}
        option={option}
        activePosSlug={activePosSlug}
        dashboardSelectHandler={dashboardSelectHandler}
        activeMenu={activeMenu}
        onSelect={onSelect}
        createBookingAllowed={createBookingAllowed}
        isAllHotelsSelected={isAllHotelsSelected}
      />
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const NavigationBarList = ({ dashboardSelectHandler, activePage, posName }) => {
  const dispatch = useDispatch();

  const [DASHBOARD_NAVIGATION_OPTIONS_MODIFIED, SET_DASHBOARD_NAVIGATION_OPTIONS_MODIFIED] =
    useState([]);
  const [openSections, setOpenSections] = useState({});
  const [hoveredSection, setHoveredSection]   = useState(null);
  const [flyoutTop,      setFlyoutTop]         = useState(0);

  // Timer ref so we can cancel the close delay when mouse re-enters
  const closeTimer = useRef(null);

  const {
    viewBookingAllowed, viewClientAllowed, viewRatedAndAvailabiltyAllowed,
    editHouseKeepingAllowed, createPOSAllowed, currentUserPosIds,
    viewLedgerAllowed, viewPaymentAllowed, viewExpenseAllowed, viewInvoiceAllowed,
    isOwner, viewStocksAllowed, editStocksAllowed,
  } = useSelector((store) => store.usersRestriction);

  const { isMobileScreen }    = useSelector((store) => store.appHeader);
  const { isAllHotelsSelected } = useSelector((store) => store.login);
  const { activePosSlug }     = useSelector((store) => store.posSetting);
  const { hotelDetails }      = useSelector((store) => store.login);
  const { menuDrawer, activeMenu } = useSelector((store) => store.app);
  const { createBookingAllowed }  = useSelector((store) => store.usersRestriction);

  const navigate = useNavigate();
  const screen = useScreen();

  useEffect(() => {
    const filtered = DASHBOARD_NAVIGATION_OPTIONS?.filter((option) => {
      let isAllowed = false;

      if (option?.title === "Bookings" && viewBookingAllowed) {
        option.link =
          hotelDetails?.bookingView === "calender" ? "bookingReservation" : "bookings";
        isAllowed = true;
      }

      if (option?.title === "Channel Manager" && viewRatedAndAvailabiltyAllowed) isAllowed = true;
      if (option?.title === "Clients"         && viewClientAllowed)              isAllowed = true;
      if (option?.title === "Housekeeping"    && editHouseKeepingAllowed)        isAllowed = true;

      if (
        (option?.title === "POS" && Array?.isArray(currentUserPosIds) && currentUserPosIds?.length > 0) ||
        isOwner
      )
        isAllowed = true;

      if (option?.title === "Ledger"        && viewLedgerAllowed)  isAllowed = true;
      if (option?.title === "Payments"      && viewPaymentAllowed) isAllowed = true;
      if (option?.title === "Expense"       && viewExpenseAllowed) isAllowed = true;
      if (option?.title === "Invoice"       && viewInvoiceAllowed) isAllowed = true;
      if (option?.title === "Manage Stocks" && (viewStocksAllowed || editStocksAllowed)) isAllowed = true;

      if (
        (option?.title === "Expense" || option?.title === "Invoice" || option?.title === "Payments") &&
        isMobileScreen
      )
        isAllowed = false;

      if (option?.title === "Dashboard" || option?.title === "Reports") isAllowed = true;
      if (option?.title === "Booking Engine") isAllowed = true;

      return isAllowed;
    }).filter((option) =>
      isAllHotelsSelected ? activeMenuWithHotelFilters.includes(option.title) : true
    );

    SET_DASHBOARD_NAVIGATION_OPTIONS_MODIFIED(filtered);

    const activeSectionKey = getActiveSectionKey(filtered, activeMenu);
    const initial = {};
    NAVIGATION_SECTIONS.forEach((s) => { initial[s.key] = s.key === activeSectionKey; });
    setOpenSections(initial);
  }, [
    DASHBOARD_NAVIGATION_OPTIONS, isAllHotelsSelected, createPOSAllowed,
    currentUserPosIds, isOwner, screen, hotelDetails, activeMenu,
  ]);

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleCollapsedSectionClick = (key) => {
    const next = {};
    NAVIGATION_SECTIONS.forEach((s) => { next[s.key] = s.key === key; });
    setOpenSections(next);
    dispatch(updateMenuDrawer(false));
  };

  // ── Flyout hover handlers (with delay so mouse can travel icon → flyout) ────
  const openFlyout = (key, e) => {
    clearTimeout(closeTimer.current);
    setFlyoutTop(e.currentTarget.getBoundingClientRect().top);
    setHoveredSection(key);
  };

  const scheduleFlyoutClose = () => {
    closeTimer.current = setTimeout(() => setHoveredSection(null), 180);
  };

  const cancelFlyoutClose = () => clearTimeout(closeTimer.current);

  const activeSectionKey = getActiveSectionKey(DASHBOARD_NAVIGATION_OPTIONS_MODIFIED, activeMenu);

  // ── Collapsed sidebar ───────────────────────────────────────────────────────
  if (menuDrawer) {
    return (
      <div className="flex flex-col pt-[65px]" data-testid="collapsed-nav">
        {NAVIGATION_SECTIONS.map((section) => {
          const sectionItems = DASHBOARD_NAVIGATION_OPTIONS_MODIFIED.filter(
            (o) => o.section === section.key
          );
          if (sectionItems.length === 0) return null;

          return (
            <div
              key={section.key}
              data-testid={`nav-section-${section.key}`}
              onMouseEnter={(e) => openFlyout(section.key, e)}
              onMouseLeave={scheduleFlyoutClose}
            >
              <CollapsedSectionIcon
                section={section}
                isActive={activeSectionKey === section.key}
                onClick={() => handleCollapsedSectionClick(section.key)}
              />

              {hoveredSection === section.key && (
                <SectionFlyout
                  section={section}
                  sectionItems={sectionItems}
                  activePosSlug={activePosSlug}
                  dashboardSelectHandler={dashboardSelectHandler}
                  activeMenu={activeMenu}
                  top={flyoutTop}
                  onMouseEnter={cancelFlyoutClose}
                  onMouseLeave={scheduleFlyoutClose}
                  onSelect={() => setHoveredSection(null)}
                  createBookingAllowed={createBookingAllowed}
                  isAllHotelsSelected={isAllHotelsSelected}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Expanded sidebar ────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col pt-[65px]">
      {NAVIGATION_SECTIONS.map((section) => {
        const sectionItems = DASHBOARD_NAVIGATION_OPTIONS_MODIFIED.filter(
          (option) => option.section === section.key
        );
        if (sectionItems.length === 0) return null;

        const isOpen = openSections[section.key];

        return (
          <div key={section.key} data-testid={`nav-section-${section.key}`}>
            <SectionLabel
              label={section.label}
              isOpen={isOpen}
              onClick={() => toggleSection(section.key)}
            />
            {isOpen && (
              <div data-testid={`nav-section-items-${section.key}`}>
                {sectionItems.map((option) => (
                  <NavigationItem
                    key={option.id}
                    navigationOptions={option}
                    dashboardSelectHandler={dashboardSelectHandler}
                    activePosSlug={activePosSlug}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NavigationBarList;
